import { ZIPF_BAND_LEVEL_KEYS, occurrenceZipfBreakdown, zipfToBand } from "./lexicon-zipf-bands.js";

/** @type {Record<string, string>} */
const VARIANT_TO_CANON = {};
for (var bi = 0; bi < ZIPF_BAND_LEVEL_KEYS.length; bi++) {
  var k = ZIPF_BAND_LEVEL_KEYS[bi];
  VARIANT_TO_CANON[String(k || "").trim().toLowerCase()] = k;
  VARIANT_TO_CANON[String(k || "").trim().replace(/\s+/g, "").toLowerCase()] = k;
}
VARIANT_TO_CANON["c1+"] = ZIPF_BAND_LEVEL_KEYS[8];

/**
 * Canonicalize a user-provided band key (labels from ZIPF_LEGEND_NAME / texts).
 * @param {string} raw
 * @returns {string | null}
 */
export function canonWordBandKey(raw) {
  var s = String(raw || "").trim();
  if (!s) return null;
  var c = VARIANT_TO_CANON[s.toLowerCase()];
  if (c) return c;
  var nsp = s.replace(/\s+/g, "").toLowerCase();
  c = VARIANT_TO_CANON[nsp];
  if (c) return c;
  if (/^[0-8]$/.test(s)) return ZIPF_BAND_LEVEL_KEYS[Number(s)];
  if (/^[0-8]\b/.test(nsp)) return ZIPF_BAND_LEVEL_KEYS[Number(nsp.charAt(0))];
  return null;
}

/**
 * Normalize lexicon-ui text aggregates: object keyed by band, or numeric array length 9
 * ({@link ZIPF_BAND_LEVEL_KEYS} order).
 * @param {unknown} raw
 * @returns {Record<string, number>|null}
 */
export function normalizeLexiconWordsPerBand(raw) {
  /** @type {Record<string, number>} */
  var out = {};

  function addCanon(canonKey, num) {
    if (!canonKey) return;
    var v = typeof num === "number" ? num : Number(num);
    if (!Number.isFinite(v) || v < 0) v = 0;
    var prev = Number(out[canonKey]) || 0;
    out[canonKey] = prev + Math.round(v);
  }

  if (raw == null) return Object.keys(out).length ? out : null;
  if (Array.isArray(raw)) {
    if (raw.length !== ZIPF_BAND_LEVEL_KEYS.length) return null;
    for (var aj = 0; aj < raw.length; aj++) {
      addCanon(ZIPF_BAND_LEVEL_KEYS[aj], raw[aj]);
    }
    return Object.keys(out).length ? out : null;
  }
  if (typeof raw !== "object") return null;
  /** @type {Record<string, unknown>} */
  var o = raw;
  for (var kk in o) {
    if (!Object.prototype.hasOwnProperty.call(o, kk)) continue;
    var canon = canonWordBandKey(kk);
    if (canon) addCanon(canon, o[kk]);
  }
  return Object.keys(out).length ? out : null;
}

/** @param {unknown} surface */
function surfaceFormKey(surface) {
  var s = String(surface || "");
  if (s.indexOf("{") >= 0) s = s.split("{", 1)[0];
  return s.trim().split(/\s+/).join(" ").toLowerCase();
}

/**
 * First occurrence wins per lexicon `lemmaId`, else per normalized surface (no `lemmaId`).
 * Mirrors `lexicon/lexicon_py/levels/fine.py` `dedupe_entries_by_lemma`.
 * @param {unknown[]} entries
 * @returns {Record<string, unknown>[]}
 */
export function dedupeLexiconEntriesByLemma(entries) {
  /** @type {Record<string, boolean>} */
  var seen = Object.create(null);
  /** @type {Record<string, unknown>[]} */
  var out = [];
  if (!Array.isArray(entries)) return out;
  for (var i = 0; i < entries.length; i++) {
    var entry = entries[i];
    if (!entry || typeof entry !== "object") continue;
    /** @type {Record<string, unknown>} */
    var row = /** @type {Record<string, unknown>} */ (entry);
    var lid = row.lemmaId;
    var key =
      lid != null && String(lid).trim()
        ? "id:" + String(lid).trim()
        : "surf:" + surfaceFormKey(row.surface);
    if (seen[key]) continue;
    seen[key] = true;
    out.push(row);
  }
  return out;
}

/** @param {Record<string, unknown>} entry */
function zipfScoreForFineBand(entry) {
  var bd = occurrenceZipfBreakdown(entry);
  if (Number.isFinite(bd.combinedZipf)) return bd.combinedZipf;
  if (Number.isFinite(bd.wordfreqZipf)) return bd.wordfreqZipf;
  if (Number.isFinite(bd.corpusZipf)) return bd.corpusZipf;
  return NaN;
}

/**
 * Unique-lemma fine-band counts from lexicon UI `entries[]` (deduped by lemmaId).
 * @param {unknown[]} entries
 * @returns {Record<string, number>|null}
 */
export function lemmaLevelCountsFromEntries(entries) {
  var reps = dedupeLexiconEntriesByLemma(entries);
  /** @type {Record<string, number>} */
  var buckets = Object.create(null);
  for (var i = 0; i < reps.length; i++) {
    var z = zipfScoreForFineBand(reps[i]);
    var bandIdx = zipfToBand(z);
    if (bandIdx < 0 || bandIdx >= ZIPF_BAND_LEVEL_KEYS.length) continue;
    var key = ZIPF_BAND_LEVEL_KEYS[bandIdx];
    buckets[key] = (Number(buckets[key]) || 0) + 1;
  }
  return normalizeLexiconWordsPerBand(buckets);
}

/**
 * @param {Record<string, number>|null|undefined} a
 * @param {Record<string, number>|null|undefined} b
 * @returns {Record<string, number>|null}
 */
export function mergeWordsPerBand(a, b) {
  var na = normalizeLexiconWordsPerBand(a);
  var nb = normalizeLexiconWordsPerBand(b);
  if (!na) return nb;
  if (!nb) return na;
  /** @type {Record<string, number>} */
  var out = {};
  for (var i = 0; i < ZIPF_BAND_LEVEL_KEYS.length; i++) {
    var k = ZIPF_BAND_LEVEL_KEYS[i];
    var v = (Number(na[k]) || 0) + (Number(nb[k]) || 0);
    if (v) out[k] = v;
  }
  return Object.keys(out).length ? out : null;
}

/**
 * Per-band integer average (e.g. sum of scenes ÷ scene count). Zeros omitted per band after round.
 * @param {Record<string, number>|null|undefined} bands
 * @param {number} divisor
 * @returns {Record<string, number>|null}
 */
export function averageWordsPerBandByCount(bands, divisor) {
  var nb = normalizeLexiconWordsPerBand(bands);
  var n = typeof divisor === "number" ? Math.round(divisor) : Number(divisor);
  if (!nb || !(n > 1)) return nb;
  /** @type {Record<string, number>} */
  var raw = {};
  for (var i = 0; i < ZIPF_BAND_LEVEL_KEYS.length; i++) {
    var k = ZIPF_BAND_LEVEL_KEYS[i];
    var part = Math.round((Number(nb[k]) || 0) / n);
    if (part > 0) raw[k] = part;
  }
  return normalizeLexiconWordsPerBand(raw);
}

function sumBands(norm, keysList) {
  var s = 0;
  for (var qi = 0; qi < keysList.length; qi++) {
    s += bandCount(norm, keysList[qi]);
  }
  return s;
}

/** Top fine band key; legacy JSON may still use `C1+`. */
var TOP_FINE = ZIPF_BAND_LEVEL_KEYS[8];

/**
 * @param {Record<string, number>} norm
 * @param {string} k
 * @returns {number}
 */
function bandCount(norm, k) {
  var v = Number(norm[k]) || 0;
  if (!v && k === TOP_FINE) v = Number(norm["C1+"]) || 0;
  return v;
}

/**
 * Map Zipf-labelled band word counts to three stripe segments for a content CEFR row.
 * Tier 1 = darker band, tier 2 = lighter, tier 3 = rare / outside parent sub-bands (grey).
 * @param {Record<string, number>|null|undefined} normalizedBands
 * @param {string} levelLower `data-card-level` (a1, b2, c1, …)
 * @returns {{ tier1: number, tier2: number, tier3: number } | null}
 */
export function lexiconBandsToStripeTriple(normalizedBands, levelLower) {
  var norm = normalizeLexiconWordsPerBand(normalizedBands);
  if (!norm) return null;
  var lv = String(levelLower || "").toLowerCase();

  /** @returns {number} */
  function n(k) {
    return bandCount(norm, k);
  }

  var t1;
  var t2;
  var restKeys;
  if (lv.startsWith("a1")) {
    t1 = n("A1.1");
    t2 = n("A1.2");
    restKeys = ["A2.1", "A2.2", "B1.1", "B1.2", "B2.1", "B2.2", TOP_FINE];
  } else if (lv.startsWith("a2")) {
    t1 = n("A2.1");
    t2 = n("A2.2");
    restKeys = ["B1.1", "B1.2", "B2.1", "B2.2", TOP_FINE];
  } else if (lv.startsWith("b1")) {
    t1 = n("B1.1");
    t2 = n("B1.2");
    restKeys = ["B2.1", "B2.2", TOP_FINE];
  } else if (lv.startsWith("b2") || lv.startsWith("c")) {
    t1 = n("B2.1");
    t2 = n("B2.2");
    restKeys = [TOP_FINE];
  } else {
    return null;
  }

  var t3 = sumBands(norm, restKeys);
  if (!t1 && !t2 && !t3) return null;
  return { tier1: t1, tier2: t2, tier3: t3 };
}

/**
 * Coarse label shown on the left of the collapsed stripe (matches hub badge tier).
 * @param {string} levelLower
 */
export function levelRowLabel(levelLower) {
  var lv = String(levelLower || "").toLowerCase();
  if (lv.lastIndexOf("a1", 0) === 0) return "A1";
  if (lv.lastIndexOf("a2", 0) === 0) return "A2";
  if (lv.lastIndexOf("b1", 0) === 0) return "B1";
  if (lv.lastIndexOf("b2", 0) === 0) return "B2";
  if (lv.lastIndexOf("c", 0) === 0) return "C1";
  return "A1";
}

/**
 * Maps `data-card-level` to `.badge--*` suffix (C row reuses B2 purple strip).
 * @param {string} levelLower
 */
export function levelBadgeMod(levelLower) {
  var lv = String(levelLower || "").toLowerCase();
  if (lv.lastIndexOf("a1", 0) === 0) return "a1";
  if (lv.lastIndexOf("a2", 0) === 0) return "a2";
  if (lv.lastIndexOf("b1", 0) === 0) return "b1";
  if (lv.lastIndexOf("b2", 0) === 0) return "b2";
  if (lv.lastIndexOf("c", 0) === 0) return "b2";
  return "a1";
}

/**
 * Last fine-band index (in {@link ZIPF_BAND_LEVEL_KEYS}) included in the content row before grey.
 * @param {string} levelLower
 */
export function contentLevelBandCutIndex(levelLower) {
  var lv = String(levelLower || "").toLowerCase();
  if (lv.lastIndexOf("a1", 0) === 0) return 1;
  if (lv.lastIndexOf("a2", 0) === 0) return 3;
  if (lv.lastIndexOf("b1", 0) === 0) return 5;
  if (lv.lastIndexOf("b2", 0) === 0 || lv.lastIndexOf("c", 0) === 0) return 7;
  return 1;
}

/**
 * Tooltip / label for lemmas above the story row (grey bucket), e.g. A1 → "A2–C2".
 * @param {string} levelLower
 */
export function greyBandsRangeLabel(levelLower) {
  var lv = String(levelLower || "").toLowerCase();
  if (lv.lastIndexOf("a1", 0) === 0) return "A2–C2";
  if (lv.lastIndexOf("a2", 0) === 0) return "B1–C2";
  if (lv.lastIndexOf("b1", 0) === 0) return "B2–C2";
  if (lv.lastIndexOf("b2", 0) === 0 || lv.lastIndexOf("c", 0) === 0) return "C1/C2";
  return "A2–C2";
}

/**
 * @param {Record<string, number>} norm
 * @param {string} levelLower
 */
function sumGreyBandCounts(norm, levelLower) {
  var cut = contentLevelBandCutIndex(levelLower);
  var grey = 0;
  for (var j = cut + 1; j < ZIPF_BAND_LEVEL_KEYS.length; j++) {
    grey += bandCount(norm, ZIPF_BAND_LEVEL_KEYS[j]);
  }
  return grey;
}

/**
 * Band key → hue bucket for fine-grained expanded segments.
 * @param {string} bandKey
 */
export function fineBandToBadgeMod(bandKey) {
  var k = String(bandKey || "");
  if (k === "grey") return "grey";
  if (k.indexOf("A1") === 0) return "a1";
  if (k.indexOf("A2") === 0) return "a2";
  if (k.indexOf("B1") === 0) return "b1";
  if (k.indexOf("B2") === 0) return "b2";
  if (k.indexOf("C1") === 0) return "b2";
  return "a1";
}

/**
 * Collapsed level stripe: “easy” words inside the story’s CEFR row (label), next finer stripe, grey beyond.
 * @param {Record<string, number>|null|undefined} normalizedBands
 * @param {string} levelLower
 * @returns {{ easy: number, mid: number, hard: number, total: number } | null}
 */
export function bandsToCollapsedTriple(normalizedBands, levelLower) {
  var norm = normalizeLexiconWordsPerBand(normalizedBands);
  if (!norm) return null;
  var lv = String(levelLower || "").toLowerCase();

  /** @returns {number} */
  function n(k) {
    return bandCount(norm, k);
  }

  var easy = 0;
  var mid = 0;

  if (lv.lastIndexOf("a1", 0) === 0) {
    easy = n("A1.1");
    mid = n("A1.2");
  } else if (lv.lastIndexOf("a2", 0) === 0) {
    easy = n("A1.1") + n("A1.2") + n("A2.1");
    mid = n("A2.2");
  } else if (lv.lastIndexOf("b1", 0) === 0) {
    easy = sumBands(norm, ["A1.1", "A1.2", "A2.1", "A2.2", "B1.1"]);
    mid = n("B1.2");
  } else if (lv.lastIndexOf("b2", 0) === 0 || lv.lastIndexOf("c", 0) === 0) {
    easy = sumBands(norm, ["A1.1", "A1.2", "A2.1", "A2.2", "B1.1", "B1.2", "B2.1"]);
    mid = n("B2.2");
  } else {
    return null;
  }

  var hard = sumGreyBandCounts(norm, lv);

  var total = easy + mid + hard;
  if (total <= 0) return null;
  return { easy: easy, mid: mid, hard: hard, total: total };
}

/**
 * Hover “detail” stripe: all fine bands up through the story’s row, then a grey remainder.
 * @param {Record<string, number>|null|undefined} normalizedBands
 * @param {string} levelLower
 * @returns {{ key: string, count: number }[]}
 */
export function bandsToExpandedSegments(normalizedBands, levelLower) {
  var norm = normalizeLexiconWordsPerBand(normalizedBands);
  if (!norm) return [];
  var lv = String(levelLower || "").toLowerCase();
  var cutIndex = contentLevelBandCutIndex(lv);

  /** @type {{ key: string, count: number }[]} */
  var out = [];
  for (var i = 0; i <= cutIndex; i++) {
    var bk = ZIPF_BAND_LEVEL_KEYS[i];
    var c = bandCount(norm, bk);
    if (c > 0) out.push({ key: bk, count: c });
  }
  var grey = sumGreyBandCounts(norm, lv);
  if (grey > 0) out.push({ key: "grey", count: grey });
  return out;
}
